import { useState, useRef, useEffect } from "react";
import DOMPurify from "dompurify";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import {
  Send, Save, Clock, Paperclip, Loader2, X, Bold, Italic, Underline, List, ListOrdered, Link2, PenSquare, Landmark, Smile, Minimize2, Users,
} from "lucide-react";
import { RecipientInput } from "@/components/RecipientInput";
import { MailingListDialog } from "@/components/MailingListDialog";

// Unisce email a una stringa separata da virgola, senza duplicati.
const mergeEmails = (existing, add) => {
  const seen = new Set();
  const out = [];
  [...(existing || "").split(","), ...add].map((s) => s.trim()).filter(Boolean).forEach((e) => {
    const k = e.toLowerCase();
    if (!seen.has(k)) { seen.add(k); out.push(e); }
  });
  return out.join(", ");
};

const TOOLBAR = [
  { cmd: "bold", icon: Bold, label: "Grassetto" },
  { cmd: "italic", icon: Italic, label: "Corsivo" },
  { cmd: "underline", icon: Underline, label: "Sottolineato" },
  { cmd: "insertUnorderedList", icon: List, label: "Elenco puntato" },
  { cmd: "insertOrderedList", icon: ListOrdered, label: "Elenco numerato" },
];

export const ComposeDialog = ({ open, onClose, onSent, initial }) => {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [sendAt, setSendAt] = useState("");
  const [rewriting, setRewriting] = useState(null);
  const [appliedTone, setAppliedTone] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [fromAccount, setFromAccount] = useState("");
  const [contacts, setContacts] = useState([]);
  const [mlBuildings, setMlBuildings] = useState([]);
  const [mlOpen, setMlOpen] = useState(false);
  const [mlTarget, setMlTarget] = useState("to");
  const editorRef = useRef(null);
  const draftId = useRef(null);
  const inReplyTo = useRef(null);
  const initialRef = useRef({ to: "", cc: "", bcc: "", subject: "", html: "", attN: 0 });
  const [confirmClose, setConfirmClose] = useState(false);

  useEffect(() => {
    if (!open) return;
    const i = initial || {};
    draftId.current = i.draft_id || null;
    inReplyTo.current = i.in_reply_to || null;
    setTo(i.to || "");
    setCc(i.cc || "");
    setBcc(i.bcc || "");
    setShowCc(!!i.cc);
    setShowBcc(!!i.bcc);
    setSubject(i.subject || "");
    setAttachments(i.attachments || []);
    setScheduling(false);
    setSendAt("");
    setAppliedTone(null);
    // Carica le caselle collegate per la scelta del mittente
    api.get("/accounts/summary").then((r) => {
      const accs = r.data?.accounts || [];
      setAccounts(accs);
      const preferred = i.from_account_id && accs.some((a) => a.id === i.from_account_id)
        ? i.from_account_id
        : (accs[0]?.id || "");
      setFromAccount(preferred);
    }).catch(() => { setAccounts([]); setFromAccount(""); });
    // Carica la rubrica dei condòmini (autocompletamento + liste)
    api.get("/contacts/address-book").then((r) => {
      setContacts(r.data?.contacts || []);
      setMlBuildings(r.data?.buildings || []);
    }).catch(() => { setContacts([]); setMlBuildings([]); });
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = DOMPurify.sanitize(i.body_html || "");
        initialRef.current = {
          to: i.to || "", cc: i.cc || "", bcc: i.bcc || "", subject: i.subject || "",
          html: editorRef.current.innerHTML, attN: (i.attachments || []).length,
        };
      }
    }, 30);
  }, [open, initial]);

  const exec = (cmd) => { document.execCommand(cmd, false, null); editorRef.current?.focus(); };

  // Firma dinamica: mostra nell'editor la firma della casella mittente scelta e
  // la aggiorna quando cambi la casella nel selettore "Da".
  const applySig = (sigHtml) => {
    const ed = editorRef.current;
    if (!ed) return;
    const clean = sigHtml ? DOMPurify.sanitize(sigHtml) : "";
    let el = ed.querySelector("[data-sig]");
    if (el) {
      if (clean) el.innerHTML = clean; else el.remove();
    } else if (clean) {
      const wrap = document.createElement("div");
      wrap.setAttribute("data-sig", "true");
      wrap.innerHTML = clean;
      const spacer = document.createElement("div");
      spacer.innerHTML = "<br>";
      const bq = ed.querySelector("blockquote");
      if (bq) { ed.insertBefore(spacer, bq); ed.insertBefore(wrap, bq); }
      else { ed.appendChild(spacer); ed.appendChild(wrap); }
    }
    initialRef.current.html = ed.innerHTML;
  };

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const acc = accounts.find((a) => a.id === fromAccount);
      applySig(acc?.signature_html || "");
    }, 60);
    return () => clearTimeout(t);
  }, [fromAccount, accounts, open]);

  const addLink = () => {
    const url = window.prompt("Inserisci URL del link:", "https://");
    if (url) { document.execCommand("createLink", false, url); editorRef.current?.focus(); }
  };

  const getHtml = () => DOMPurify.sanitize(editorRef.current?.innerHTML || "");
  const getText = () => editorRef.current?.innerText || "";

  const rewriteTone = async (tone) => {
    const editor = editorRef.current;
    if (!editor) return;
    // Mantieni la citazione dell'email originale: riscrivi SOLO la parte scritta dall'utente
    // (tutto ciò che precede il blockquote di citazione).
    const nodes = Array.from(editor.childNodes);
    const bqIndex = nodes.findIndex((n) => n.nodeName === "BLOCKQUOTE");
    const replyWrap = document.createElement("div");
    const quoteWrap = document.createElement("div");
    nodes.forEach((n, i) => {
      if (bqIndex >= 0 && i >= bqIndex) quoteWrap.appendChild(n.cloneNode(true));
      else replyWrap.appendChild(n.cloneNode(true));
    });
    const replyText = (replyWrap.innerText || "").trim();
    if (!replyText) { toast.error("Scrivi prima un testo da riscrivere"); return; }
    setRewriting(tone);
    try {
      const r = await api.post("/compose/rewrite", { text: replyText, tone });
      const rewritten = DOMPurify.sanitize(r.data.html || "");
      const quoteHtml = bqIndex >= 0 ? DOMPurify.sanitize(quoteWrap.innerHTML) : "";
      if (editor) {
        editor.innerHTML = quoteHtml ? `${rewritten}<div><br></div>${quoteHtml}` : rewritten;
      }
      setAppliedTone(tone);
      toast.success(`Testo riscritto in tono ${tone}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Riscrittura fallita"); }
    finally { setRewriting(null); }
  };

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`"${f.name}" supera i 10 MB`); continue; }
      setUploading(true);
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await api.post("/attachments/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((a) => [...a, r.data]);
      } catch { toast.error(`Caricamento di "${f.name}" fallito`); }
      finally { setUploading(false); }
    }
  };

  const removeAtt = (id) => setAttachments((a) => a.filter((x) => x.id !== id));

  const openMailingList = (target) => { setMlTarget(target); setMlOpen(true); };
  const onMailingConfirm = (emails) => {
    if (mlTarget === "cc") { setShowCc(true); setCc((v) => mergeEmails(v, emails)); }
    else if (mlTarget === "bcc") { setShowBcc(true); setBcc((v) => mergeEmails(v, emails)); }
    else setTo((v) => mergeEmails(v, emails));
  };

  const payload = (mode) => ({
    to, cc, bcc, subject, body: getText(), body_html: getHtml(),
    attachments: attachments.map((a) => a.id), mode,
    draft_id: draftId.current || undefined,
    in_reply_to: inReplyTo.current || undefined,
    from_account_id: fromAccount || undefined,
    send_at: mode === "schedule" ? new Date(sendAt).toISOString() : undefined,
  });

  const doSend = async () => {
    if (!to.trim()) return toast.error("Inserisci almeno un destinatario");
    setBusy(true);
    try {
      const r = await api.post("/communications/compose", payload("send"));
      toast.success(`Email inviata a ${r.data.sent_to}`);
      onSent?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Invio fallito. Verifica la configurazione SMTP."); }
    finally { setBusy(false); }
  };

  const doDraft = async () => {
    setBusy(true);
    try {
      const r = await api.post("/communications/compose", payload("draft"));
      draftId.current = r.data.id;
      toast.success("Bozza salvata");
      onSent?.(); onClose();
    } catch { toast.error("Salvataggio bozza fallito"); }
    finally { setBusy(false); }
  };

  const doSchedule = async () => {
    if (!to.trim()) return toast.error("Inserisci almeno un destinatario");
    if (!sendAt) return toast.error("Scegli data e ora di invio");
    if (new Date(sendAt) <= new Date()) return toast.error("La data deve essere futura");
    setBusy(true);
    try {
      const r = await api.post("/communications/compose", payload("schedule"));
      toast.success(`Invio programmato per ${new Date(r.data.send_at).toLocaleString("it-IT")}`);
      onSent?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Programmazione fallita"); }
    finally { setBusy(false); }
  };

  const isDirty = () => {
    const init = initialRef.current;
    const curHtml = editorRef.current?.innerHTML || "";
    return to !== init.to || cc !== init.cc || bcc !== init.bcc ||
      subject !== init.subject || curHtml !== init.html || attachments.length !== init.attN;
  };

  const handleRequestClose = () => {
    if (busy) return;
    if (isDirty()) setConfirmClose(true);
    else onClose();
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleRequestClose(); }}>
      <DialogContent data-testid="compose-dialog" className="max-w-3xl w-[95vw] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PenSquare className="h-4 w-4" />{draftId.current ? "Modifica bozza" : (inReplyTo.current ? "Rispondi" : "Nuova email")}</DialogTitle>
          <DialogDescription className="sr-only">Componi una email con destinatari, allegati e opzioni di invio.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {accounts.length > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-500 w-12 shrink-0">Da</span>
              <select data-testid="compose-from" value={fromAccount} onChange={(e) => setFromAccount(e.target.value)}
                className="flex-1 rounded-md h-9 border border-gray-200 bg-white px-2 text-sm outline-none focus:border-[#007BFF]">
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.name ? `${a.name} — ${a.email}` : a.email}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 w-12 shrink-0">A</span>
            <RecipientInput testid="compose-to" value={to} onChange={setTo} contacts={contacts} placeholder="destinatario@email.it (separa con virgola)" />
            <div className="flex gap-1 shrink-0">
              {!showCc && <button data-testid="compose-add-cc" onClick={() => setShowCc(true)} className="text-xs text-[#007BFF] font-medium px-1.5">Cc</button>}
              {!showBcc && <button data-testid="compose-add-bcc" onClick={() => setShowBcc(true)} className="text-xs text-[#007BFF] font-medium px-1.5">Ccn</button>}
            </div>
          </div>
          <div className="flex justify-end -mt-1">
            <button type="button" data-testid="compose-open-mailing-list" onClick={() => openMailingList("to")}
              className="inline-flex items-center gap-1.5 text-xs text-[#007BFF] font-medium hover:underline">
              <Users className="h-3.5 w-3.5" />Liste condòmini
            </button>
          </div>
          {showCc && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-500 w-12 shrink-0">Cc</span>
              <RecipientInput testid="compose-cc" value={cc} onChange={setCc} contacts={contacts} placeholder="copia@email.it" />
            </div>
          )}
          {showBcc && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-500 w-12 shrink-0">Ccn</span>
              <RecipientInput testid="compose-bcc" value={bcc} onChange={setBcc} contacts={contacts} placeholder="copia nascosta@email.it" />
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 w-12 shrink-0">Oggetto</span>
            <Input data-testid="compose-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Oggetto" className="rounded-md h-9" />
          </div>

          {/* Rich text toolbar */}
          <div className="border border-gray-200 rounded-md overflow-hidden">
            <div className="flex items-center gap-0.5 border-b border-gray-200 bg-gray-50 px-2 py-1.5">
              {TOOLBAR.map((t) => (
                <button key={t.cmd} type="button" data-testid={`editor-${t.cmd}`} title={t.label} onMouseDown={(e) => { e.preventDefault(); exec(t.cmd); }}
                  className="h-7 w-7 flex items-center justify-center rounded hover:bg-gray-200 text-gray-600 transition-colors duration-150">
                  <t.icon className="h-4 w-4" />
                </button>
              ))}
              <button type="button" data-testid="editor-link" title="Link" onMouseDown={(e) => { e.preventDefault(); addLink(); }}
                className="h-7 w-7 flex items-center justify-center rounded hover:bg-gray-200 text-gray-600 transition-colors duration-150">
                <Link2 className="h-4 w-4" />
              </button>
              <div className="ml-auto flex items-center gap-1">
                <span className="text-[11px] text-gray-400 hidden sm:inline mr-0.5">Riscrivi con AI:</span>
                <button type="button" data-testid="rewrite-istituzionale" title="Riscrivi in tono istituzionale"
                  onClick={() => rewriteTone("istituzionale")} disabled={!!rewriting} aria-pressed={appliedTone === "istituzionale"}
                  className={`inline-flex items-center gap-1 h-7 px-2 rounded border text-[11px] font-medium disabled:opacity-50 transition-colors duration-150 ${appliedTone === "istituzionale" ? "border-[#007BFF] bg-[#007BFF]/10 text-[#007BFF]" : "border-gray-200 text-gray-700 hover:bg-gray-200"}`}>
                  {rewriting === "istituzionale" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Landmark className="h-3.5 w-3.5" />}Istituzionale
                </button>
                <button type="button" data-testid="rewrite-informale" title="Riscrivi in tono informale"
                  onClick={() => rewriteTone("informale")} disabled={!!rewriting} aria-pressed={appliedTone === "informale"}
                  className={`inline-flex items-center gap-1 h-7 px-2 rounded border text-[11px] font-medium disabled:opacity-50 transition-colors duration-150 ${appliedTone === "informale" ? "border-[#007BFF] bg-[#007BFF]/10 text-[#007BFF]" : "border-gray-200 text-gray-700 hover:bg-gray-200"}`}>
                  {rewriting === "informale" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Smile className="h-3.5 w-3.5" />}Informale
                </button>
                <button type="button" data-testid="rewrite-sintetico" title="Rendi il testo più sintetico e breve"
                  onClick={() => rewriteTone("sintetico")} disabled={!!rewriting} aria-pressed={appliedTone === "sintetico"}
                  className={`inline-flex items-center gap-1 h-7 px-2 rounded border text-[11px] font-medium disabled:opacity-50 transition-colors duration-150 ${appliedTone === "sintetico" ? "border-[#007BFF] bg-[#007BFF]/10 text-[#007BFF]" : "border-gray-200 text-gray-700 hover:bg-gray-200"}`}>
                  {rewriting === "sintetico" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Minimize2 className="h-3.5 w-3.5" />}Sintetico
                </button>
              </div>
            </div>
            <div ref={editorRef} data-testid="compose-editor" contentEditable suppressContentEditableWarning
              className="min-h-[220px] max-h-[340px] overflow-y-auto p-4 text-sm leading-relaxed focus:outline-none prose-sm" />
          </div>

          {/* Attachments */}
          <div>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-1.5 text-sm text-[#007BFF] font-medium cursor-pointer hover:underline">
                <input type="file" multiple className="hidden" data-testid="compose-attach-input" onChange={onFile} />
                <Paperclip className="h-4 w-4" />Allega file
              </label>
              {uploading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
              <span className="text-xs text-gray-400">Max 10 MB per file</span>
            </div>
            {attachments.length > 0 && (
              <div data-testid="compose-attachments" className="flex flex-wrap gap-2 mt-2">
                {attachments.map((a) => (
                  <span key={a.id} className="inline-flex items-center gap-1.5 text-xs bg-gray-100 border border-gray-200 rounded-md pl-2.5 pr-1.5 py-1 text-gray-700">
                    <Paperclip className="h-3 w-3" />{a.filename}
                    <button data-testid={`compose-remove-att-${a.id}`} onClick={() => removeAtt(a.id)} className="hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Schedule */}
          {scheduling && (
            <div data-testid="compose-schedule-row" className="flex items-center gap-3 bg-blue-50/40 border border-blue-100 rounded-md p-3">
              <Clock className="h-4 w-4 text-[#007BFF] shrink-0" />
              <span className="text-sm text-gray-600 shrink-0">Invia il:</span>
              <input type="datetime-local" data-testid="compose-schedule-input" value={sendAt} onChange={(e) => setSendAt(e.target.value)}
                className="flex-1 rounded-md border border-gray-200 px-2 py-1.5 text-sm" />
            </div>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <Button data-testid="compose-save-draft" onClick={doDraft} disabled={busy} variant="outline" className="rounded-md">
            <Save className="h-4 w-4 mr-2" />Salva bozza
          </Button>
          <Button data-testid="compose-toggle-schedule" onClick={() => setScheduling((s) => !s)} disabled={busy} variant="outline" className={`rounded-md ${scheduling ? "border-[#007BFF] text-[#007BFF]" : ""}`}>
            <Clock className="h-4 w-4 mr-2" />{scheduling ? "Annulla programmazione" : "Programma"}
          </Button>
          {scheduling ? (
            <Button data-testid="compose-schedule-send" onClick={doSchedule} disabled={busy} className="rounded-md bg-[#007BFF] hover:bg-[#0056B3]">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Clock className="h-4 w-4 mr-2" />}Programma invio
            </Button>
          ) : (
            <Button data-testid="compose-send" onClick={doSend} disabled={busy} className="rounded-md bg-[#007BFF] hover:bg-[#0056B3]">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Invia
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
      <AlertDialogContent data-testid="compose-close-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>Salvare la bozza?</AlertDialogTitle>
          <AlertDialogDescription>
            Hai del testo non inviato. Vuoi salvarlo nelle bozze prima di uscire?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-wrap gap-2">
          <Button variant="ghost" data-testid="compose-close-discard"
            onClick={() => { setConfirmClose(false); onClose(); }} className="rounded-md text-red-600 hover:text-red-700">
            Non salvare
          </Button>
          <Button variant="outline" data-testid="compose-close-cancel"
            onClick={() => setConfirmClose(false)} className="rounded-md">
            Annulla
          </Button>
          <Button data-testid="compose-close-save-draft" disabled={busy}
            onClick={async () => { setConfirmClose(false); await doDraft(); }}
            className="rounded-md bg-[#007BFF] hover:bg-[#0056B3]">
            <Save className="h-4 w-4 mr-2" />Salva in bozze
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <MailingListDialog open={mlOpen} onClose={() => setMlOpen(false)} buildings={mlBuildings} onConfirm={onMailingConfirm} />
    </>
  );
};
